/**
 * @module lib/audience/gtm-packages
 */
import logger from "utils/logger";
import isNil from "lodash/isNil";
import every from "lodash/every";
import { isVisible, isHidden } from "ui/element-is-visible";
import { toArray } from "utils/utils";

const debugTag = "[GTM-PACKAGES]";

/**
 * @method gtmPackages
 * @description Extra analytics context info for story cards clicked within packages
 * @param {HTMLElement} clicked - the DOM element that was clicked
 * @returns {Object|null} data to add to the analytics payload
 * @property {Object.presentation} - containing comma separated list of presentation formats
 * @property {Object.variantName} - total number of links inside a package
 */
export function gtmPackages(clicked) {
  // container selectors
  const select = {
    base: ".c-package", // All packages
    card: ".c-card" // Story cards
  };

  // parent package container is required
  const pack = clicked.closest(select.base);
  if (isNil(pack)) {
    return null; // clicked is not a story within a package
  }

  // package inner element selectors
  const elements = {
    label: ".c-card__label",
    storyPhoto: ".c-image[data-photo='story']",
    authorPhoto: ".c-image[data-photo='author']",
    videoIcon: ".c-card__video-duration",
    authorName: ".c-card__author",
    deck: ".c-card__dek"
  };

  let dom = {}; // select all the internal package elements
  Object.keys(elements).forEach(key => {
    dom[key] = clicked.querySelector(elements[key]);
  });

  // construct the presentation string
  const photo = getPhoto(dom);
  const presentation = [
    getLabel(dom),
    isVisible(dom.deck) ? "deck" : null,
    photo,
    isVisible(dom.videoIcon) ? "video-icon" : null,
    isVisible(dom.authorName) ? "author-name" : null,
    getHeadline(dom, photo)
  ].filter(i => i); // filter out nulls

  // count all the visible cards within the package
  const cards = toArray(pack.querySelectorAll(select.card)).map(isVisible);

  const pkgData = {
    presentation: presentation.join(","),
    variantName: cards.filter(i => i).length // filter out invisible cards
  };

  logger.info(`${debugTag}`, pkgData, presentation, cards);
  return pkgData;
}

/**
 * @method getLabel
 * @param {Object} d - collection of package elements
 * @description The label portion of the presentation string
 * @returns {String|null}
 */
function getLabel(d) {
  let label = null;
  if (isVisible(d.label)) {
    label = `label:${d.label.textContent}`;
    // Determine whether the label is "reversed" (i.e. has a background colour)
    const bgColor = window.getComputedStyle(d.label).backgroundColor;
    if (bgColor !== "rgba(0, 0, 0, 0)" && bgColor !== "transparent") {
      label = `${label}:reversed`;
    }
  }
  return label;
}

/**
 * @method getPhoto
 * @param {Object} d - collection of package elements
 * @description The photo portion of the presentation string
 * @returns {String|null}
 */
function getPhoto(d) {
  // Determine which photo, if any, is visible
  const storyPhoto = getSpecificPhoto(d.storyPhoto) ? "photo" : null;
  const authorPhoto = getSpecificPhoto(d.authorPhoto) ? "author-photo" : null;
  return storyPhoto || authorPhoto || null;
}

/**
 * @method getSpecificPhoto
 * @param {Object} element - photo DOM element
 * @description Returns true if the photo element is visible
 * @returns {Boolean}
 */
function getSpecificPhoto(element) {
  if (isHidden(element)) {
    return false;
  }
  // Should we traverse the DOM all the way up to the .c-package element
  // instead of explicitly specifying these 2 potential parent elements?
  const parentContainers = [
    element.closest(".c-image-container"),
    element.closest(".c-card__media")
  ].filter(i => i); // filter out nulls

  return parentContainers.every(isVisible);
}

/**
 * @method getHeadline
 * @param {Object} d - collection of package elements
 * @param {String|null} photo - if a visible photo exists, this will not be null
 * @description The headline portion of the presentation string
 * @returns {String|null}
 */
function getHeadline(d, photo) {
  const allHidden = every([d.label, d.authorName, d.deck], isHidden) && !photo;
  return allHidden ? "headline-only" : null;
}
